import React, { ReactNode } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import ExternalLink from 'components/external-link';
import { useLocale } from 'state/intl';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { primitive } from 'styles/constants/primitives';
import { isWeb } from 'utils/environment';

export const CA_ENGLISH_DELIVERY_TERMS_URL = 'https://www.timhortons.ca/delivery-terms';
export const CA_FRENCH_DELIVERY_TERMS_URL = 'https://www.timhortons.ca/modalites-de-livraison';
export const US_DELIVERY_TERMS_URL = 'https://www.timhortons.com/delivery-terms';

export type DeliveryPriceDisclaimerPlacement = 'locator' | 'menu' | 'product' | 'cart-total';

const isUsLocator = (region: string, placement: DeliveryPriceDisclaimerPlacement) =>
  region === 'US' && placement === 'locator';

const Disclaimer = styled.p<{
  $placement: DeliveryPriceDisclaimerPlacement;
  $region: string;
}>`
  color: ${props =>
    isUsLocator(props.$region, props.$placement) ? '#654d4d' : Styles.color.black};
  font-size: ${props => {
    if (isUsLocator(props.$region, props.$placement)) {
      return '12px';
    }
    if (props.$region === 'US') {
      return '0.9rem';
    }
    return props.$placement === 'cart-total' ? '0.75rem' : '0.8rem';
  }};
  line-height: ${props => (isUsLocator(props.$region, props.$placement) ? '16px' : '1.25rem')};
  margin: ${props => {
    if (isUsLocator(props.$region, props.$placement)) {
      return '0';
    }
    if (props.$region === 'CA') {
      return props.$placement === 'cart-total' ? '0.5rem 0 0' : '0.75rem 0';
    }
    return props.$placement === 'product' ? '0 auto 0.75rem' : '0.75rem auto 0';
  }};
  max-width: ${props => (props.$region === 'US' ? '32rem' : 'none')};
  padding: ${props => {
    if (isUsLocator(props.$region, props.$placement)) {
      return `${primitive.$spacing2} ${primitive.$spacing4}`;
    }
    return props.$region === 'US' && props.$placement !== 'product' ? '0 1rem' : '0';
  }};
  text-align: ${props => {
    if (isUsLocator(props.$region, props.$placement)) {
      return 'left';
    }
    return props.$region === 'US' ? 'center' : 'inherit';
  }};
`;

const DisclaimerLink = styled(ExternalLink)`
  color: ${Styles.color.primary};
  text-decoration: underline;
`;

const US_DELIVERY_PRICE_DISCLAIMER_MESSAGE_ID = 'usDeliveryPriceDisclaimer';

interface ResolveShowDeliveryPriceDisclaimerInput {
  isWebPlatform?: boolean;
  placement: DeliveryPriceDisclaimerPlacement;
  region: string;
  serviceMode: ServiceMode | null;
}

export const resolveShowDeliveryPriceDisclaimer = ({
  isWebPlatform = isWeb,
  placement,
  region,
  serviceMode,
}: ResolveShowDeliveryPriceDisclaimerInput): boolean => {
  if (region === 'CA') {
    if (!isWebPlatform) {
      return false;
    }

    return (
      placement === 'locator' || (placement !== 'menu' && serviceMode === ServiceMode.DELIVERY)
    );
  }

  if (region !== 'US') {
    return false;
  }

  if (placement === 'locator') {
    return isWebPlatform;
  }

  return placement !== 'cart-total' && serviceMode === ServiceMode.DELIVERY;
};

export const useShowDeliveryPriceDisclaimer = (
  placement: DeliveryPriceDisclaimerPlacement
): boolean => {
  const { region } = useLocale();
  const { serviceMode } = useServiceModeContext();

  return resolveShowDeliveryPriceDisclaimer({
    placement,
    region,
    serviceMode,
  });
};

interface DeliveryPriceDisclaimerProps {
  placement: DeliveryPriceDisclaimerPlacement;
}

export const DeliveryPriceDisclaimer: React.FC<DeliveryPriceDisclaimerProps> = ({ placement }) => {
  const { formatMessage } = useIntl();
  const { region } = useLocale();
  const show = useShowDeliveryPriceDisclaimer(placement);

  if (!show) {
    return null;
  }

  const isCanada = region === 'CA';
  const messageId = isCanada
    ? placement === 'cart-total'
      ? 'caCartDeliveryTerms'
      : 'caDeliveryFeeAndTermsDisclosure'
    : US_DELIVERY_PRICE_DISCLAIMER_MESSAGE_ID;

  return (
    <Disclaimer
      $placement={placement}
      $region={region}
      data-testid={
        isCanada
          ? `ca-delivery-disclosure-${placement}`
          : `us-delivery-${placement}-price-disclaimer`
      }
    >
      {formatMessage(
        { id: messageId },
        isCanada
          ? {
              enLink: (chunks: ReactNode) => (
                <DisclaimerLink href={CA_ENGLISH_DELIVERY_TERMS_URL}>{chunks}</DisclaimerLink>
              ),
              frLink: (chunks: ReactNode) => (
                <DisclaimerLink href={CA_FRENCH_DELIVERY_TERMS_URL}>{chunks}</DisclaimerLink>
              ),
            }
          : {
              link: (chunks: ReactNode) => (
                <DisclaimerLink href={US_DELIVERY_TERMS_URL}>{chunks}</DisclaimerLink>
              ),
            }
      )}
    </Disclaimer>
  );
};
